import React, { useMemo } from 'react';
import { Card, Typography, Spin, Alert } from 'antd';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useQuoteConversionByRegion } from '../hooks';

const { Title, Text } = Typography;

interface RegionalPerformanceTabProps {
  dateRange?: string;
  region?: number;
}

export const RegionalPerformanceTab: React.FC<RegionalPerformanceTabProps> = ({
  dateRange = 'all',
  region,
}) => {
  // Fetch quote conversion by region data from API
  const { data, isLoading, error } = useQuoteConversionByRegion(dateRange, region);

  // Transform API data to match the existing chart structure
  const regionalPerformance = useMemo(() => {
    if (!data?.data) return [];

    return data.data.map(region => ({
      region: region.regionLabel,
      quotes: region.totalQuotes,
      conversion: region.regionConversionRate,
      revenue: 0, // Revenue data not available in the API response
    }));
  }, [data]);
  return (
    <Card>
      <Title level={4}>Regional Performance Comparison</Title>
      <Text type="secondary" className="block mb-4">
        Quote volume and conversion rates by region
      </Text>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '100px 0' }}>
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert
          message="Error Loading Data"
          description="Failed to load regional performance data. Please try again."
          type="error"
          showIcon
        />
      ) : (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={regionalPerformance}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="region" />
            <YAxis yAxisId="left" />
            <YAxis yAxisId="right" orientation="right" />
            <Tooltip />
            <Legend />
            <Bar yAxisId="left" dataKey="quotes" fill="#22D3EE" name="Quote Volume" />
            <Bar yAxisId="right" dataKey="conversion" fill="#EF4444" name="Conversion %" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};
